import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';
import { BaseDestroyable } from '@core/base-destroyable';
import { forkJoin } from 'rxjs';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { TimberService } from '@logging/timber.service';
import { BiAction, BiObjectType } from '@logging/interfaces/bi.enums';
import { CrownDesign, SettingsService } from '@shared/settings.service';
import { ISettingsData } from '@interfaces/ISettingsData';
import { ShellContextService } from 'app/services/shell-context/shell-context.service';

@Component({
	selector: 'eup-crown-ai-settings-modal',
	templateUrl: './crownAISettingsModal.component.html',
	styleUrls: ['./crownAISettingsModal.component.scss']
})
export class CrownAISettingsModalComponent extends BaseDestroyable implements OnInit, OnDestroy {

	distanceToNeighbor: number;
	distanceToAntagonist: number;
	arealAdjacentContacts: number;
	preferSmoothVsDetails: number;
	crownAIListOptions: string[];
	crownAIListOptionsPinned: string[];
	styleTranslation: Record<string, string> = {};
	style: string | null = null;
	readonly styleDefault = 'select';
	crownDesign: CrownDesign = {
		distanceToNeighbor: 0,
		distanceToAntagonist: 0,
		arealAdjacentContacts: 0,
		preferSmoothVsDetails: 0,
		style: this.styleDefault
	}

	@ViewChild('crownAISettingsModal') crownAISettingsModal: ModalDirective;

	constructor(
		public translateService: TranslateService,
		private cdr: ChangeDetectorRef,
		private timberService: TimberService,
		private shellContextService: ShellContextService,
		private settingsService: SettingsService
	) {
		super();
	}

	ngOnInit(): void {
		forkJoin([
			this.translateService.get('CrownAI-Style-List'),
			this.translateService.get('CrownAI-Style-List-Pinned')
		]).pipe(
			tap(([list, pinned]: [Record<string, string>, Record<string, string>]) => {
				this.styleTranslation = { ...pinned, ...list };
				this.crownAIListOptionsPinned = Object.values(pinned).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })); 
				this.crownAIListOptions = Object.values(list).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
			}),
			switchMap(() => this.shellContextService.selectedCompanyId$.pipe(
				filter((companyId: number) => !!companyId && companyId > 0)
			)),
			switchMap((companyId: number) => this.settingsService.getSettings(companyId, true, false)),
			takeUntil(this.componentAlive$)
		).subscribe((settingsRes: ISettingsData) => {
			this.crownDesign = settingsRes.userSettings.crownDesign ?? this.crownDesign;
			this.distanceToNeighbor = this.crownDesign.distanceToNeighbor;
			this.distanceToAntagonist = this.crownDesign.distanceToAntagonist;
			this.arealAdjacentContacts = this.crownDesign.arealAdjacentContacts * 100;
			this.preferSmoothVsDetails = this.crownDesign.preferSmoothVsDetails * 100;
			this.style = this.styleTranslation[this.crownDesign.style] ?? this.styleTranslation[this.styleDefault];
		});
	}

	ngModelChange(event: string) {
		this.style = event;

		const styleKey = Object.keys(this.styleTranslation).find(key => this.styleTranslation[key] === this.style);
		this.settingChanged('style', styleKey);

		this.timberService.userActionEvent({
			locationParentObject: 'DesignSuiteSettings',
			objectName: 'StyleDropdown',
			objectType: BiObjectType.Combobox,
			action: this.style ? BiAction.Select : BiAction.Deselect,
			selectedValue: styleKey,
			automationId: '',
		});
	}
	show(): void {
		this.cdr.detectChanges();
		this.crownAISettingsModal.show();
	}

	hide(): void {
		this.crownAISettingsModal.hide();
	}

	settingChanged(fieldName: string, value: any) {
		const fieldsToNormalize = ['arealAdjacentContacts', 'preferSmoothVsDetails'];
		const serverValue = fieldsToNormalize.includes(fieldName) ? value / 100 : value;
		this.crownDesign[fieldName] = serverValue;
		this.settingsService.setSettings('CrownDesign', JSON.stringify(this.crownDesign)).subscribe();
	}
}
